import { useSafeAreaInsets } from "react-native-safe-area-context"
import { View } from "react-native"
import { Alert } from "./ui/confirm"
import { useEffect, useState } from "react"
import * as ExpoUpdates from "expo-updates"

import { ar } from "@src/i18n/ar"
import { ENV } from "@src/utils/env"
import versionCompare from "@src/utils/version-compare"
import { firestore, doc, onSnapshot } from "@src/utils/firebase"

import appJson from "../app.json"
import { MaintenanceScreen } from "./maintenance"
import { Text } from "./core"

type Config = { lastestVersion: string; latestRuntime: number; maintenance: boolean; minRuntime: number; minVersion: string }
const runtimeVersion = Number.parseInt(appJson.expo.runtimeVersion, 10)

const spinner = { enabled: true, color: "#FFFFFF", size: "large" } as const
const RELOAD_OPTIONS = {
  reloadScreenOptions: { imageResizeMode: "contain", image: "../assets/splash.png", backgroundColor: "#D53C2B", spinner },
} as const

export function VersionCheck({ children }: { children: React.ReactNode }) {
  const insets = useSafeAreaInsets()
  const [config, setConfig] = useState<Config>()

  useEffect(() => {
    if (__DEV__) return

    const unsub = onSnapshot(doc(firestore, `${ENV.channel}.config`, "config"), (snapshot) => {
      const config = snapshot.data() as Config
      setConfig(config)
    })

    return () => unsub()
  }, [])

  const currentVersionJsx = (
    <Text
      className="text-end absolute text-[16px] bottom-0 web:bottom-2 end-[10] text-gray-500 opacity-30 pointer-events-none z-10"
      style={{ paddingBottom: insets.bottom }}
    >
      {runtimeVersion} / {appJson.expo.version} / {ENV.channel}
    </Text>
  )

  if (config?.maintenance) {
    return <MaintenanceScreen />
  }

  if (config?.minVersion) {
    const minVersion = versionCompare(appJson.expo.version, config.minVersion)
    if (minVersion === -1) {
      if (runtimeVersion !== config.minRuntime) {
        return <BlockToUpdate />
      }
      return <UpdateForeGround />
    }
  }

  if (config?.lastestVersion) {
    const latestVersion = versionCompare(appJson.expo.version, config.lastestVersion)
    if (latestVersion === -1) {
      if (runtimeVersion === config.latestRuntime) {
        return (
          <>
            {children}
            <UpdateBackground />
          </>
        )
      }
    }
  }

  return (
    <>
      {children}
      {currentVersionJsx}
    </>
  )
}

function BlockToUpdate() {
  return (
    <View className="flex-1 justify-center items-center bg-red-600">
      <Text className="text-center text-2xl font-bold text-white">{ar.blockToUpdate}</Text>
    </View>
  )
}

function UpdateForeGround() {
  useEffect(() => {
    async function effect() {
      const update = await ExpoUpdates.checkForUpdateAsync()
      if (update.isAvailable) {
        await ExpoUpdates.fetchUpdateAsync()
        Alert.alert(ar.updateReady, ar.install, [
          {
            text: ar.install,
            onPress: () => ExpoUpdates.reloadAsync(RELOAD_OPTIONS),
          },
        ])
      } else {
        Alert.alert(ar.noUpdatesAvailable)
      }
    }

    effect()
  }, [])

  return (
    <View className={`flex-1 justify-center items-center bg-blue-500/80`}>
      <Text>{ar.pleaseWait}</Text>
    </View>
  )
}

function UpdateBackground() {
  const insets = useSafeAreaInsets()

  useEffect(() => {
    async function effect() {
      const update = await ExpoUpdates.checkForUpdateAsync()
      if (update.isAvailable) {
        await ExpoUpdates.fetchUpdateAsync()

        Alert.alert(ar.updateReady, ar.install, [
          { text: ar.later, style: "cancel" },
          {
            text: ar.install,
            onPress: () => ExpoUpdates.reloadAsync(RELOAD_OPTIONS),
          },
        ])
      } else {
        Alert.alert(ar.noUpdatesAvailable)
      }
    }

    effect()
  }, [])

  return (
    <Text
      className="absolute text-[16px] web:bottom-5 end-[20] text-gray-500 opacity-30 text-left pointer-events-none z-10"
      style={{ bottom: 0, paddingBottom: insets.bottom + 5 * 4 }}
    >
      {appJson.expo.runtimeVersion} / {appJson.expo.version}
    </Text>
  )
}
